import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  User,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useState } from "react";
import Price from "../Currency/Price";
import { useTranslation } from "react-i18next";

interface CartItem {
  id: string;
  productId: string;
  name: string;
  sku: string;
  price: number;
  quantity: number;
  total: number;
  stock: number;
  variationId?: string;
  variationAttributes?: {
    [key: string]: string;
  };
}

interface CustomerInfo {
  name: string;
  phone: string;
  city: string;
  zone: string;
  street: string;
  buildingNumber: string;
  unitNumber: string;
}

const CartContent = ({
  cart,
  customerInfo,
  setCustomerInfo,
  updateQuantity,
  removeFromCart,
  clearCart,
  handleCheckout,
  getSubtotal,
  getTax,
  getDiscount,
  getTotal,
  tax,
  setTax,
  taxType,
  setTaxType,
  discount,
  setDiscount,
  discountType,
  setDiscountType,
  isMobile = false,
}: {
  cart: CartItem[];
  customerInfo: CustomerInfo;
  setCustomerInfo: (info: CustomerInfo) => void;
  updateQuantity: (id: string, qty: number) => void;
  removeFromCart: (id: string) => void;
  clearCart: () => void;
  handleCheckout: () => void;
  getSubtotal: () => number;
  getTax: () => number;
  getDiscount: () => number;
  getTotal: () => number;
  tax: number;
  setTax: (tax: number) => void;
  taxType: "percentage";
  setTaxType: (type: "percentage") => void;
  discount: number;
  setDiscount: (discount: number) => void;
  discountType: "amount" | "percentage";
  setDiscountType: (type: "amount" | "percentage") => void;
  isMobile?: boolean;
}) => {
  const { t } = useTranslation();
  const [showAddressFields, setShowAddressFields] = useState(false);
  const parseAmount = (value: string) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  };

  return (
    <>
      {!isMobile && (
        <div className="bg-gradient-to-r from-black via-gray-700 to-black px-4 sm:px-6 py-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <ShoppingCart className="w-6 h-6" />
            {t("Current Order")}
          </h2>
          <p className="text-gray-300 text-sm mt-1">
            {cart.length} {t("items")}
          </p>
        </div>
      )}

      {/* Customer Info */}
      <div className="p-4 border-b border-gray-200 space-y-3">
        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
            {t("Customer Name (Optional)")}
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={customerInfo.name}
              onChange={(e) =>
                setCustomerInfo({ ...customerInfo, name: e.target.value })
              }
              className="w-full pl-10 pr-3 py-2 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm"
              placeholder={t("Enter customer name")}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
            {t("Mobile Number")} (Optional)
          </label>
          <input
            type="tel"
            value={customerInfo.phone}
            onChange={(e) =>
              setCustomerInfo({ ...customerInfo, phone: e.target.value })
            }
            className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm"
            placeholder={t("Enter mobile number")}
          />
        </div>

        <div className="pt-1">
          <button
            type="button"
            onClick={() => setShowAddressFields((isVisible) => !isVisible)}
            className="w-full flex items-center justify-between text-left text-xs sm:text-sm font-semibold text-gray-700 py-1"
            aria-expanded={showAddressFields}
            title={showAddressFields ? t("Collapse Address") : t("Expand Address")}
          >
            <span>{t("Address")} (Optional)</span>
            {showAddressFields ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
          {showAddressFields && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              <label className="text-xs font-semibold text-gray-700">
                {t("City")}
                <input
                  type="text"
                  value={customerInfo.city}
                  onChange={(e) =>
                    setCustomerInfo({ ...customerInfo, city: e.target.value })
                  }
                  className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm font-normal"
                />
              </label>
              <label className="text-xs font-semibold text-gray-700">
                {t("Zone")}
                <input
                  type="text"
                  value={customerInfo.zone}
                  onChange={(e) =>
                    setCustomerInfo({ ...customerInfo, zone: e.target.value })
                  }
                  className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm font-normal"
                />
              </label>
              <label className="text-xs font-semibold text-gray-700">
                {t("Street")}
                <input
                  type="text"
                  value={customerInfo.street}
                  onChange={(e) =>
                    setCustomerInfo({ ...customerInfo, street: e.target.value })
                  }
                  className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm font-normal"
                />
              </label>
              <label className="text-xs font-semibold text-gray-700">
                {t("Building Number")}
                <input
                  type="text"
                  value={customerInfo.buildingNumber}
                  onChange={(e) =>
                    setCustomerInfo({
                      ...customerInfo,
                      buildingNumber: e.target.value,
                    })
                  }
                  className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm font-normal"
                />
              </label>
              <label className="text-xs font-semibold text-gray-700">
                {t("Unit Number")}
                <input
                  type="text"
                  value={customerInfo.unitNumber}
                  onChange={(e) =>
                    setCustomerInfo({
                      ...customerInfo,
                      unitNumber: e.target.value,
                    })
                  }
                  className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm font-normal"
                />
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Cart Items */}
      <div className="flex-1 overflow-y-auto p-4">
        {cart.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <ShoppingCart className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-sm sm:text-base">
              {t("Cart is empty")}
            </p>
            <p className="text-gray-400 text-xs sm:text-sm mt-2">
              {t("Add products to get started")}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {cart.map((item) => (
              <div key={item.id} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 text-sm">
                      {item.name}
                    </h3>
                    <p className="text-xs text-gray-500">{item.sku}</p>
                    {item.variationAttributes && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Object.entries(item.variationAttributes).map(
                          ([key, value]) => (
                            <span
                              key={key}
                              className="text-xs text-gray-600 bg-gray-200 px-2 py-0.5 rounded"
                            >
                              {key}: {value}
                            </span>
                          ),
                        )}
                      </div>
                    )}
                    <p className="text-sm font-bold text-black mt-1">
                      <Price amount={Number(item.price)} />
                      {"each"}
                    </p>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-300">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="p-2 hover:bg-gray-100 rounded-l-lg transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) =>
                        updateQuantity(item.id, parseInt(e.target.value) || 0)
                      }
                      className="w-12 text-center border-0 focus:outline-none text-sm font-semibold"
                      min="1"
                      max={item.stock}
                    />
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      disabled={item.quantity >= item.stock}
                      className="p-2 hover:bg-gray-100 rounded-r-lg transition-colors disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <span className="text-base sm:text-lg font-bold text-black">
                    <Price amount={Number(item.total)} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cart Summary */}
      {cart.length > 0 && (
        <div className="border-t-2 border-gray-200 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                {t("Tax")}
              </label>
              <div className="flex gap-1">
                <input
                  type="number"
                  value={tax}
                  onChange={(e) => setTax(parseAmount(e.target.value))}
                  className="w-full px-2 py-1.5 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm"
                  min="0"
                  step="0.01"
                />
                <select
                  value={taxType}
                  onChange={(e) => setTaxType(e.target.value as "percentage")}
                  className="px-2 py-1.5 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm"
                >
                  {/* <option value="amount">{t("Amount")}</option> */}
                  <option value="percentage">%</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                {t("Discount")}
              </label>
              <div className="flex gap-1">
                <input
                  type="number"
                  value={discount}
                  onChange={(e) => setDiscount(parseAmount(e.target.value))}
                  className="w-full px-2 py-1.5 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm"
                  min="0"
                  step="0.01"
                />
                <select
                  value={discountType}
                  onChange={(e) =>
                    setDiscountType(e.target.value as "amount" | "percentage")
                  }
                  className="px-2 py-1.5 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none text-sm"
                >
                  <option value="amount">{t("Amount")}</option>
                  <option value="percentage">%</option>
                </select>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t("Subtotal")}:</span>
              <span className="font-semibold text-gray-800">
                <Price amount={Number(getSubtotal())} />
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">
                {t("Tax")}
                {taxType === "percentage" ? ` (${tax}%)` : ""}:
              </span>
              <span className="font-semibold text-gray-800">
                <Price amount={Number(getTax())} />
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">
                {t("Discount")}
                {discountType === "percentage" ? ` (${discount}%)` : ""}:
              </span>
              <span className="font-semibold text-red-600">
                -<Price amount={Number(getDiscount())} />
              </span>
            </div>
            <div className="flex justify-between text-base sm:text-lg border-t-2 border-gray-200 pt-2">
              <span className="font-bold text-gray-800">{t("Total")}:</span>
              <span className="font-bold text-black text-lg sm:text-xl">
                <Price amount={Number(getTotal())} />
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={clearCart}
              className="flex-1 py-2.5 sm:py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-semibold text-sm sm:text-base"
            >
              {t("Clear")}
            </button>
            <button
              onClick={handleCheckout}
              className="flex-1 py-2.5 sm:py-3 bg-black text-white rounded-lg hover:bg-gray-700 transition-all transform hover:-translate-y-0.5 hover:shadow-lg font-semibold text-sm sm:text-base"
            >
              {t("Checkout")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CartContent;
